import { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { BackArrowIcon, NextArrowIcon, XIcon } from "src/assets/Icons";

interface HeroGalleryLightboxProps {
  images: string[];
  initialIndex: number;
  title: string;
  subtitle?: string;
  onClose: () => void;
}

const HeroGalleryLightbox: FC<HeroGalleryLightboxProps> = ({ images, initialIndex, title, subtitle, onClose }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const total = images.length;

  const prev = () => setCurrentIndex((i) => (i - 1 + total) % total);
  const next = () => setCurrentIndex((i) => (i + 1) % total);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handleKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return createPortal(
    <div onClick={onClose} className="fixed inset-0 z-50 flex flex-col bg-background-overlay/80" role="dialog" aria-modal="true" aria-label={title}>
      {/* Header */}
      <div className="flex items-start justify-between px-6 pt-6 text-font-inverted">
        <div onClick={stop} className="min-w-0">
          <h2 className="truncate text-lg font-semibold">{title}</h2>
          {subtitle && <p className="truncate text-sm text-font-inverted/70">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={(e) => {
            stop(e);
            onClose();
          }}
          aria-label={t("Hotels.booking.closeGallery")}
          className="rounded-full p-2 text-font-inverted transition hover:bg-font-inverted/10"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Image */}
      <div className="flex flex-1 items-center justify-center px-2 py-2">
        <img
          onClick={stop}
          src={images[currentIndex]}
          alt={`${title} ${currentIndex + 1} of ${total}`}
          className="h-full w-full rounded-md object-contain"
          style={{ maxHeight: "70vh", minHeight: "60vh" }}
        />
      </div>

      {/* Footer pill */}
      {total > 1 && (
        <div className="flex justify-center px-6 pb-8">
          <div onClick={stop} className="flex items-center gap-4 rounded-xl bg-background-overlay/70 px-4 py-2 text-font-inverted">
            <button type="button" onClick={prev} aria-label={t("Hotels.booking.previousImage")} className="p-1.5 hover:bg-font-inverted/10">
              <BackArrowIcon />
            </button>
            <span className="min-w-15 border-l border-r border-s-border-default border-e-border-default px-4 text-center text-sm font-medium tracking-wide">
              {t("Hotels.booking.imageOfTotal", { current: currentIndex + 1, total })}
            </span>
            <button type="button" onClick={next} aria-label={t("Hotels.booking.nextImage")} className="p-1.5 hover:bg-font-inverted/10">
              <NextArrowIcon />
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default HeroGalleryLightbox;
